import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Scale, Building2, Network, Shield, Briefcase, Heart, Factory,
  ArrowRight, CheckCircle, FileCheck
} from 'lucide-react';

const IndustriesPage = () => {
  const industries = [
    {
      icon: Scale,
      title: 'Law Firms',
      description: 'Secure, compliant IT solutions designed for legal professionals. We understand the unique demands of legal practice management, client confidentiality, and regulatory compliance.',
      challenges: [
        'Strict confidentiality requirements',
        'Complex document management needs',
        'Multi-office coordination',
        'Court system integrations',
        'Compliance with bar association standards'
      ],
      solutions: [
        'Secure document management systems',
        'Encrypted communications',
        'Practice management integrations',
        'Disaster recovery planning',
        'CJIS compliance support'
      ]
    },
    {
      icon: Building2,
      title: 'Property Management',
      description: 'Technology solutions that help property managers operate efficiently across multiple locations. From tenant communications to building systems, we connect it all.',
      challenges: [
        'Multi-property management complexity',
        'Tenant and owner communication',
        'Building system integrations',
        'Mobile workforce support',
        'Vendor coordination'
      ],
      solutions: [
        'Property management software integration',
        'Multi-site network connectivity',
        'Building access control systems',
        'CCTV and surveillance',
        'Mobile device management'
      ]
    },
    {
      icon: Network,
      title: 'Multi-Site Businesses',
      description: 'Consistent, reliable IT infrastructure across all your locations. We specialize in connecting distributed teams and maintaining uniform standards company-wide.',
      challenges: [
        'Inconsistent technology across sites',
        'Communication gaps between locations',
        'Centralized management needs',
        'Varying local compliance requirements',
        'Remote site support'
      ],
      solutions: [
        'SD-WAN connectivity solutions',
        'Centralized monitoring and management',
        'Standardized security policies',
        'Unified communications',
        'Remote support capabilities'
      ]
    },
    {
      icon: Shield,
      title: 'Retail Stores',
      description: 'Comprehensive IT solutions for retail operations. We support point-of-sale systems, inventory management, and customer experience technology across single and multi-location stores.',
      challenges: [
        'Point-of-sale system reliability',
        'Inventory management integration',
        'Payment security (PCI compliance)',
        'Multi-location coordination',
        'Customer data protection'
      ],
      solutions: [
        'POS system support and integration',
        'Secure payment processing',
        'Network infrastructure for retail',
        'Video surveillance and loss prevention',
        'Customer WiFi and digital signage'
      ]
    },
    {
      icon: Heart,
      title: 'Healthcare & Medical',
      description: 'HIPAA-compliant IT solutions for healthcare providers. We protect patient data while enabling efficient care delivery.',
      challenges: [
        'HIPAA compliance requirements',
        'EHR system management',
        'Patient data protection',
        'Medical device integration',
        '24/7 availability requirements'
      ],
      solutions: [
        'HIPAA-compliant infrastructure',
        'Secure patient communication',
        'Medical device security',
        'Business continuity planning',
        'Staff security training'
      ]
    },
    {
      icon: Factory,
      title: 'Manufacturing',
      description: 'Robust IT infrastructure for manufacturing operations. We support production systems, supply chain management, and operational technology while ensuring minimal downtime.',
      challenges: [
        'Production system uptime requirements',
        'OT/IT network integration',
        'Supply chain coordination',
        'Inventory and ERP systems',
        'Industrial IoT security'
      ],
      solutions: [
        'Network segmentation for OT systems',
        'Industrial control system security',
        'ERP and MES integration',
        'Production monitoring systems',
        'Backup and disaster recovery'
      ]
    },
  ];

  const complianceFrameworks = [
    { name: 'NIST', description: 'National Institute of Standards and Technology' },
    { name: 'CJIS', description: 'Criminal Justice Information Services' },
    { name: 'HIPAA', description: 'Health Insurance Portability and Accountability' },
    { name: 'NDAA', description: 'National Defense Authorization Act' },
    { name: 'SOC 2', description: 'Service Organization Control' },
    { name: 'PCI DSS', description: 'Payment Card Industry Data Security Standard' },
  ];

  return (
    <div data-testid="industries-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-mimo-primary to-slate-900 text-white py-20 md:py-28">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-extrabold font-heading mb-6">
              Industries We Serve
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Deep expertise across sectors that demand the highest standards of security, reliability, and compliance. We understand your industry's unique challenges.
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="space-y-16">
            {industries.map((industry, index) => (
              <div 
                key={industry.title}
                className="grid lg:grid-cols-2 gap-12 items-start"
                data-testid={`industry-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-mimo-primary to-mimo-accent flex items-center justify-center">
                      <industry.icon className="w-7 h-7 text-white" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold font-heading text-slate-900">
                      {industry.title}
                    </h2>
                  </div>
                  <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                    {industry.description}
                  </p>
                  <Link 
                    to="/contact" 
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Discuss Your Needs
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
                
                <div className={`grid md:grid-cols-2 gap-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="bg-slate-50 rounded-xl p-6">
                    <h3 className="font-bold font-heading text-slate-900 mb-4 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                        <span className="text-red-600 text-sm">!</span>
                      </span>
                      Challenges
                    </h3>
                    <ul className="space-y-2">
                      {industry.challenges.map((challenge, i) => (
                        <li key={i} className="text-slate-600 text-sm flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
                          {challenge}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-mimo-primary/5 rounded-xl p-6">
                    <h3 className="font-bold font-heading text-slate-900 mb-4 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </span>
                      Our Solutions
                    </h3>
                    <ul className="space-y-2">
                      {industry.solutions.map((solution, i) => (
                        <li key={i} className="text-slate-600 text-sm flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-mimo-secondary flex-shrink-0 mt-0.5" />
                          {solution}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Frameworks */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              Compliance Expertise
            </h2>
            <p className="text-lg text-slate-600">
              We help businesses meet and maintain compliance with major regulatory frameworks
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {complianceFrameworks.map((framework, index) => (
              <div 
                key={framework.name}
                className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow"
                data-testid={`compliance-${index}`}
              >
                <FileCheck className="w-8 h-8 text-mimo-primary mx-auto mb-3" />
                <p className="font-bold font-heading text-slate-900">{framework.name}</p>
                <p className="text-xs text-slate-500 mt-1">{framework.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-br from-mimo-primary to-mimo-accent text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading mb-4">
            Don't See Your Industry?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            We work with businesses across many sectors. Contact us to discuss your specific needs and how we can help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-mimo-primary hover:bg-slate-100 rounded-full px-8 py-3 font-semibold transition-all inline-flex items-center justify-center gap-2"
            >
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a 
              href="tel:201-473-4443" 
              className="border-2 border-white text-white hover:bg-white hover:text-mimo-primary rounded-full px-8 py-3 font-semibold transition-all"
            >
              Call 201-473-4443
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;